import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Scissors, Flower2, Users, ChevronRight } from "lucide-react";
import DahliasShell from "../components/dahlias/DahliasShell";
import InterestModal from "../components/dahlias/InterestModal";
import PatternBand from "../components/dahlias/PatternBand";
import { useSiteContentMap, useSiteText, useSiteImage } from "../store/useSiteContent";
import CmsText from "../components/CmsText";

const VINTAGE_PATTERN = "/photos/patterns/dahlia-pattern-4.jpg";

const WORKSHOPS = [
  {
    icon: Scissors,
    label: "Tuber Dividing",
    blurb:
      "Hands-on, in the shed. We'll show you how to lift, divide and label your tubers so every eye finds a home next season.",
    season: "Winter",
    wobble: "wobble-a",
    accent: "var(--berry)",
  },
  {
    icon: Flower2,
    label: "Picking & Arranging",
    blurb:
      "An afternoon in the rows: cutting, conditioning, and building a soft, gathered bunch you'll be proud to take home.",
    season: "Spring",
    wobble: "wobble-b",
    accent: "var(--berry-bright)",
  },
  {
    icon: Users,
    label: "Open Day & Tour",
    blurb:
      "A walk through the farm with morning tea. The story behind every row, the dahlias in their full flush, and a Q&A.",
    season: "Spring",
    wobble: "wobble-c",
    accent: "var(--dahlias-clay)",
  },
];

export default function DahliasWorkshops() {
  const [modal, setModal] = useState(null);
  const { data: cms } = useSiteContentMap();
  const heroImg = useSiteImage("workshops.hero.image", VINTAGE_PATTERN);
  const heading = useSiteText("workshops.heading", "Winter & spring\n*on the farm.*");
  const intro = useSiteText(
    "workshops.intro",
    "Small groups, on the farm, in the seasons we love best. Register your interest and we'll be in touch first when seats open."
  );
  const bandHeading = useSiteText("workshops.band.heading", "We'll write to *you first.*");
  const bandBody = useSiteText(
    "workshops.band.body",
    "Numbers are small and word travels fast. Registering your interest is the best way to make sure you hear before anyone else."
  );
  const workshops = WORKSHOPS.map((w, i) => ({
    ...w,
    label: (cms?.[`workshops.card${i + 1}.title`] || "").trim() || w.label,
    blurb: (cms?.[`workshops.card${i + 1}.blurb`] || "").trim() || w.blurb,
  }));

  return (
    <DahliasShell>
      {/* Vintage pattern hero */}
      <section
        data-testid="workshops-page"
        className="relative overflow-hidden border-b-[3px] border-[var(--berry)]/40"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImg})` }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(250,244,230,0.62) 0%, rgba(250,244,230,0.55) 50%, rgba(250,244,230,0.92) 100%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-12 md:pb-16 text-center">
          <span className="inline-flex items-center gap-3 px-4 py-1.5 wobble-pill bg-white/75 backdrop-blur-sm font-display tracking-[0.35em] text-xs text-[var(--berry-deep)]">
            ✦ WORKSHOPS ✦
          </span>
          <CmsText
            as="h1"
            text={heading}
            data-testid="workshops-heading"
            italicClass="font-display-italic text-[var(--berry)]"
            className="font-display text-4xl md:text-6xl lg:text-7xl text-[var(--dahlias-forest)] mt-6 leading-[1.05] whitespace-pre-line"
            style={{ textShadow: "0 3px 18px rgba(250,244,230,0.85)" }}
          />
          <p className="mt-7 text-lg md:text-xl text-[var(--dahlias-ink)] max-w-3xl mx-auto leading-relaxed bg-[var(--dahlias-cream)]/75 backdrop-blur-sm wobble-a inline-block px-6 py-4 whitespace-pre-line">
            {intro}
          </p>
        </div>
      </section>

      <PatternBand pattern={2} variant="thin" tint={0.3} testid="workshops-band-1" />

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {workshops.map((w) => {
            const Icon = w.icon;
            return (
              <article
                key={w.label}
                data-testid={`workshop-${w.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group relative bg-[var(--dahlias-paper)] border-[3px] p-7 md:p-8 flex flex-col shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all ${w.wobble}`}
                style={{ borderColor: w.accent }}
              >
                <span
                  aria-hidden="true"
                  className="absolute top-0 right-0 w-14 h-14 wobble-blob text-white flex items-center justify-center shadow-md"
                  style={{ backgroundColor: w.accent, transform: "translate(20%, -20%)" }}
                >
                  <Icon className="w-5 h-5" />
                </span>
                <span
                  className="font-label tracking-[0.4em] text-[10px]"
                  style={{ color: w.accent }}
                >
                  ✦ {w.season.toUpperCase()} ✦
                </span>
                <h3 className="mt-3 font-display text-3xl text-[var(--dahlias-forest)] leading-tight">
                  {w.label}
                </h3>
                <p className="mt-4 text-lg md:text-xl text-[var(--dahlias-ink)]/80 leading-relaxed flex-1">
                  {w.blurb}
                </p>
                <button
                  type="button"
                  onClick={() =>
                    setModal({
                      type: "workshop",
                      targetLabel: `${w.label} workshop`,
                    })
                  }
                  data-testid={`workshop-${w.label.toLowerCase().replace(/\s+/g, "-")}-interest`}
                  className="mt-6 inline-flex items-center justify-between gap-2 px-5 h-12 text-white wobble-pill text-base font-display tracking-wide shadow-sm hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: w.accent }}
                >
                  Register interest
                  <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              </article>
            );
          })}
        </div>
      </section>

      <PatternBand pattern={3} variant="thin" tint={0.3} testid="workshops-band-2" />

      {/* Status band */}
      <div aria-hidden="true" className="h-7 bg-[var(--dahlias-sage)] scallop-top" />
      <section className="bg-[var(--dahlias-sage)] text-[var(--dahlias-cream)]">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16 text-center">
          <span className="font-label tracking-[0.4em] text-[11px] text-[var(--dahlias-cream)]/85">
            ✦ DATES BEING SET ✦
          </span>
          <CmsText
            as="h2"
            text={bandHeading}
            italicClass="font-display-italic"
            className="mt-4 font-display text-3xl md:text-4xl leading-tight"
          />
          <p className="mt-4 text-base md:text-lg text-[var(--dahlias-cream)]/85 leading-relaxed whitespace-pre-line">
            {bandBody}
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              data-testid="workshops-general-interest"
              onClick={() =>
                setModal({ type: "workshop", targetLabel: "Workshops (any)" })
              }
              className="inline-flex items-center gap-2 px-6 h-12 bg-[var(--dahlias-cream)] text-[var(--dahlias-forest)] wobble-pill font-medium tracking-wide shadow-md"
            >
              Register general interest
            </button>
            <Link
              to="/dahlias/about"
              data-testid="workshops-contact-link"
              className="inline-flex items-center gap-2 px-6 h-12 border-2 border-[var(--dahlias-cream)]/60 hover:bg-[var(--dahlias-cream)]/10 text-[var(--dahlias-cream)] wobble-pill font-medium tracking-wide"
            >
              Or write to us
            </Link>
          </div>
        </div>
      </section>
      <div aria-hidden="true" className="h-7 bg-[var(--dahlias-sage)] scallop-bottom" />

      <InterestModal
        open={!!modal}
        onClose={() => setModal(null)}
        type={modal?.type || "workshop"}
        targetLabel={modal?.targetLabel || ""}
        subtitle="Tell us which workshop and any preferences. We'll be in touch as soon as dates are set."
      />
    </DahliasShell>
  );
}
